import React from 'react';
import {Modal} from 'antd';
import TextLoader from '../../components/ContentLoaders/TextLoader';
import TableSettingsButton from '../../components/pages/SubscriptionsPage/TableSettingsButton';
import {objectHelpers} from './object.helpers';


export const elementHelpers = {
  /**
   * Appends an optional extra class name to a base class name string.
   * @param {string} cName - Base class name.
   * @param {string|undefined} className - Additional class name to append.
   * @returns {string} Combined class name string.
   * @example
   * elementHelpers.getClassName('btn', 'btn-primary'); // 'btn btn-primary'
   */
  getClassName: (cName, className) => {
    if (className) cName += ` ${className}`;
    return cName;
  },
  /**
   * Calculates the cumulative offsetTop of a DOM element, optionally traversing a number of offset parents.
   * @param {string} elementId - CSS selector string used with document.querySelector.
   * @param {number} [parentsCount=0] - Number of offset parent levels to accumulate.
   * @returns {number} Total offsetTop value in pixels.
   * @example
   * elementHelpers.getElementOffsetTop('#my-table', 2);
   */
  getElementOffsetTop: (elementId, parentsCount = 0) => {
    let element = document.querySelector(elementId);
    let offsetTop = element?.offsetTop || 0;
    if (parentsCount && parentsCount > 0) {
      for (let i = 0; i < parentsCount; i++) {
        if (element && element.offsetParent) {
          element = element.offsetParent;
          offsetTop += element.offsetTop;
        }
      }
    }
    return offsetTop;
  },
  /**
   * Filters a columns array to only those whose dataIndex is in selectedColumns or alwaysAvailableColumnKeys.
   * @param {Object} params - Parameters object.
   * @param {string[]} [params.alwaysAvailableColumnKeys=[]] - Column keys always included regardless of selection.
   * @param {Array<Object>} [params.columns=[]] - Full list of column definition objects.
   * @param {string[]} [params.selectedColumns=[]] - User-selected column dataIndex values.
   * @returns {Array<Object>} Filtered column definitions.
   * @example
   * elementHelpers.getAvailableTableColumns({ alwaysAvailableColumnKeys: ['vendor'], columns, selectedColumns: ['status'] });
   */
  getAvailableTableColumns: ({
    alwaysAvailableColumnKeys = [],
    columns = [],
    selectedColumns = []
  }) => {
    const columnKeys = [...selectedColumns, ...alwaysAvailableColumnKeys];
    return columns.filter(column => columnKeys.includes(column?.dataIndex));
  },
  /**
   * Returns a TableSettingsButton element for selecting visible table columns, excluding always-visible columns from options.
   * @param {Object} params - Parameters object.
   * @param {string[]} [params.alwaysAvailableColumnKeys=[]] - Column keys excluded from the settings options.
   * @param {Array<Object>} [params.columns=[]] - Full list of column definition objects.
   * @param {Function} params.onChange - Callback invoked with updated selected column keys.
   * @param {string[]} params.selectedKeys - Currently selected column keys.
   * @param {React.ReactNode} params.rightSideContent - Additional content rendered inside the button.
   * @returns {React.ReactElement} TableSettingsButton element.
   * @example
   * elementHelpers.getSettingsRightSideContent({ alwaysAvailableColumnKeys: ['vendor'], columns, onChange, selectedKeys, rightSideContent: null });
   */
  getSettingsRightSideContent: ({
    alwaysAvailableColumnKeys = [],
    buttonProps = {},
    columns = [],
    onChange,
    selectedKeys,
    rightSideContent,
  }) => {
    columns = columns.filter(column => !alwaysAvailableColumnKeys.includes(column.dataIndex));
    return (
      <TableSettingsButton
        buttonProps={buttonProps}
        defaultSelectedKeys={selectedKeys}
        options={columns.map(c => ({value: c.dataIndex, label: c.title}))}
        onChange={onChange}
      >
        {rightSideContent}
      </TableSettingsButton>
    )
  },
  /**
   * Returns a class name string for a form step container, appending 'form-d-none' when the step is not visible.
   * @param {string|undefined} [className=undefined] - Optional extra class name to append.
   * @param {boolean} [isVisible=false] - Whether the step container should be visible.
   * @returns {string} Combined class name string.
   * @example
   * elementHelpers.getVisibleClassName('step-2', true); // 'form-step-container step-2'
   */
  getVisibleClassName: (className = undefined, isVisible = false) => {
    let cName = 'form-step-container';
    if (className) cName += ` ${className}`;
    if (!isVisible) cName += ' form-d-none';
    return cName;
  },
  /**
   * Opens an Ant Design confirmation modal with yes/no buttons and the provided options.
   * @param {Object} params - Parameters object spread into Modal.confirm; translate is extracted.
   * @param {Function} params.translate - i18n translation function used for button labels.
   * @returns {void}
   * @example
   * elementHelpers.showConfirmModal({ translate: t, title: 'Are you sure?', onOk: handleDelete });
   */
  showConfirmModal: ({translate, ...rest}) => {
    Modal.confirm({
      cancelText: translate('no'),
      cancelButtonProps: {size: 'large'},
      centered: true,
      okButtonProps: {size: 'large', type: 'primary'},
      okText: translate('yes'),
      ...rest
    });
  },
  /**
   * Returns the content element when enabled, otherwise returns a loader element.
   * @param {boolean} isEnable - When true the content is shown; otherwise the loader is shown.
   * @param {React.ReactNode} content - Content to display when enabled.
   * @param {React.ReactNode} [loader=<TextLoader />] - Fallback loader element.
   * @returns {React.ReactNode} Content or loader element.
   * @example
   * elementHelpers.showContentWithLoader(isLoaded, <Table />, <Spin />);
   */
  showContentWithLoader: (isEnable, content, loader = <TextLoader />) => isEnable ? content : loader,
  /**
   * Calls stopPropagation on a synthetic or native event if the event is defined.
   * @param {Event|React.SyntheticEvent|undefined} event - The event object.
   * @returns {void}
   * @example
   * elementHelpers.stopPropagation(e);
   */
  stopPropagation: (event) => event && event.stopPropagation(),
  /**
   * Updates the selected menu item keys based on the current router location.
   * @param {Object} params - Parameters object.
   * @param {Object} params.location - React Router location object with pathname.
   * @param {Array<Object>} params.items - Navigation menu item definitions with path, key, and optional children.
   * @param {string[]} params.selectedKeys - Current selected keys.
   * @param {Function} params.setSelectedKeys - State setter for selected keys.
   * @returns {void}
   * @example
   * elementHelpers.updateMenuItems({ location, items: menuItems, selectedKeys, setSelectedKeys });
   */
  updateMenuItems: ({ location, items, selectedKeys, setSelectedKeys }) => {
    let updatedSelectedKeys = [];

    for (let parent of items) {
      // Match parent item
      if (parent.path === location.pathname) {
        updatedSelectedKeys = [parent.key];
        break;
      }

      // Match child items (if they exist)
      if (Array.isArray(parent.children)) {
        for (let child of parent.children) {
          const normalizePath = (p) => (p ? p.replace(/\/+$/, '') : '');
          const childPath = normalizePath(child.path);
          const currentPath = normalizePath(location.pathname);
          if (childPath && (currentPath === childPath || currentPath.startsWith(childPath + '/'))) {
            updatedSelectedKeys = [parent.key, child.key];
            break;
          }
        }

        if (updatedSelectedKeys.length > 0) {
          break;
        }
      }
    }

    // Only update if changed
    if (!objectHelpers.arraysIsEqual(updatedSelectedKeys, selectedKeys)) {
      setSelectedKeys(updatedSelectedKeys);
    }
  },
  /**
   * Returns true when the event target is an interactive form element or inside an Ant Design select dropdown.
   * @param {Event|React.SyntheticEvent} element - Event object with a target property.
   * @returns {boolean} True if the target is INPUT, SELECT, TEXTAREA, BUTTON, or inside a select dropdown.
   * @example
   * elementHelpers.isInputOrButtonElement(keyboardEvent);
   */
  isInputOrButtonElement: (element) => {
    const tagNames = ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'];
    return (element && tagNames.includes(element?.target?.tagName)) || element?.target?.closest('.ant-select-dropdown');
  }
}
